import { useState } from "react";
import { formatDate, formatShortDate } from "./texts";

const W = 360;
const H = 200;
const PAD = { left: 70, right: 14, top: 14, bottom: 30 };

/** Three round tick values covering min…max (with some room above and below). */
function ticks(min: number, max: number): number[] {
  if (min === max) {
    const d = Math.abs(min) * 0.1 || 1;
    min -= d;
    max += d;
  }
  const raw = (max - min) / 2;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const lo = Math.floor(min / step) * step;
  const out = [lo, lo + step, lo + 2 * step];
  while (out[out.length - 1] < max) out.push(out[out.length - 1] + step);
  return out.map((v) => Math.round(v * 1000) / 1000);
}

/**
 * One series over time as a thin line with point markers: x is the date (to scale), y the value. Tapping the chart
 * selects the nearest point, whose date and value are shown under it (the last point at first). The list under the
 * chart on the exercise page has every value, so the chart needs no labels on its points.
 */
export function LineChart(props: { points: { endedAt: number; value: number }[]; format(v: number): string; label: string }) {
  const { points } = props;
  const [picked, setPicked] = useState<number>();
  const sel = picked !== undefined && picked < points.length ? picked : points.length - 1;
  const ys = ticks(Math.min(...points.map((p) => p.value)), Math.max(...points.map((p) => p.value)));
  const y0 = ys[0];
  const y1 = ys[ys.length - 1];
  const t0 = points[0].endedAt;
  const t1 = points[points.length - 1].endedAt;
  const x = (t: number) => (t1 === t0 ? (PAD.left + W - PAD.right) / 2 : PAD.left + ((t - t0) / (t1 - t0)) * (W - PAD.left - PAD.right));
  const y = (v: number) => H - PAD.bottom - ((v - y0) / (y1 - y0)) * (H - PAD.top - PAD.bottom);
  const path = points.map((p, i) => `${i ? "L" : "M"}${x(p.endedAt).toFixed(1)},${y(p.value).toFixed(1)}`).join("");

  const pick = (ev: React.PointerEvent<SVGSVGElement>) => {
    const box = ev.currentTarget.getBoundingClientRect();
    // the viewBox keeps its aspect ratio and is centred
    const scale = Math.min(box.width / W, box.height / H);
    const px = (ev.clientX - box.left - (box.width - W * scale) / 2) / scale;
    let best = 0;
    points.forEach((p, i) => { if (Math.abs(x(p.endedAt) - px) < Math.abs(x(points[best].endedAt) - px)) best = i; });
    setPicked(best);
  };
  const s = points[sel];

  return (
    <figure className="chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={props.label} onPointerDown={pick}>
        {ys.map((v) => (
          <g key={v}>
            <line className="chart-grid" x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} />
            <text className="chart-axis" x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle">{props.format(v)}</text>
          </g>
        ))}
        <text className="chart-axis" x={x(t0)} y={H - 8} textAnchor={t1 === t0 ? "middle" : "start"}>{formatShortDate(t0)}</text>
        {t1 !== t0 && <text className="chart-axis" x={x(t1)} y={H - 8} textAnchor="end">{formatShortDate(t1)}</text>}
        <line className="chart-cross" x1={x(s.endedAt)} x2={x(s.endedAt)} y1={PAD.top} y2={H - PAD.bottom} />
        <path className="chart-line" d={path} />
        {points.map((p, i) => (
          <circle key={i} className={"chart-dot" + (i === sel ? " on" : "")} cx={x(p.endedAt)} cy={y(p.value)} r={i === sel ? 6 : 4} />
        ))}
      </svg>
      <figcaption className="chart-pick">
        <span>{formatDate(s.endedAt)}</span>
        <b>{props.format(s.value)}</b>
      </figcaption>
    </figure>
  );
}
